/**
 * @typedef {Object} CrossSellingListItem
 * @property {string} product - Product handle.
 * @property {number} variant - Variant ID.
 */

class CrossSellingCartComponent extends HTMLElement {
  constructor() {
    super();

    /** @type {CrossSellingListItem[] | []} */
    this.crossSellingVariantList =
      /** @type {CrossSellingListItem[]} */ window.crossSellingVariantsJson ||
      [];

    if (
      !this.crossSellingVariantList ||
      this.crossSellingVariantList.length === 0
    ) {
      console.error("Cannot get variant list for cross selling");
      return;
    }
  }

  async connectedCallback() {
    /**
     * @type {HTMLElement | null}
     */
    this.wrapperElement = this;
    this.imageElement = this.querySelector("img");
    this.productTitleElement = this.querySelector(".product-title");
    this.productPriceElement = this.querySelector(".ajaxcart__price");
    this.compareAtPriceElement = this.querySelector("s");
    this.addToCartButton = this.querySelector(".button");
    this.closeX = this.querySelector(".cross-selling__close-button");

    if (this.closeX) {
      this.closeX.addEventListener("click", async (event) => {
        event.preventDefault();
        store.addToDeletedVariantsList(this.fullVariant.id);
        this.hideWrapper();

        // Recalculate cartFooterHeight and update cartInner bottom style
        const cartFooter = document.getElementById("cartDrawerFooter");
        const cartInner = document.getElementById("cartDrawerInner");
        if (cartFooter && cartInner) {
          let cartFooterHeight = cartFooter.offsetHeight;
          cartInner.style.bottom = `${cartFooterHeight}px`;
        }
      });
    }

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener("click", async (event) => {
        event.preventDefault();

        if (
          !this.firstValidCrossSellingVariant ||
          !this.fullProduct ||
          !this.fullVariant
        ) {
          console.error("Cannot add to cart");
          return;
        }

        const promoCode = this.getPromoCode();

        const items = [
          {
            id: this.fullVariant.id,
            quantity: 1,
            properties: {
              "Ships by": this.shipsBy?.longDate || "",
              "_promo-code": promoCode,
            },
          },
        ];

        const payload = JSON.stringify({ items });

        store.addToCart(payload);

        if (!this.wrapperElement) {
          // console.log("Cannot find cross-selling wrapper element...");
          return;
        }
        this.hideWrapper();
      });
    }

    messenger.listen(
      messenger.topics.CART_READY,
      async () => {
        this.firstValidCrossSellingVariant =
          await this.getValidCrossSellingVariant();
        if (!this.firstValidCrossSellingVariant) {
          // console.log("Cannot determine a valid cross-selling variant...");
          this.hideWrapper();
          return;
        } else {
          // // console.log(
          // //   "Rerendering cross-sell with variant: ",
          //   this.firstValidCrossSellingVariant
          // );
          this.renderVariant();
        }
      },
      "CrossSellingComponent"
    );

    const cartDrawerInner = document.querySelector(
      ".cart-drawer__inner-content"
    );
    if (cartDrawerInner) {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            // console.trace("Mutation detected");
            if (
              this.wrapperElement &&
              this.wrapperElement.classList.contains(
                "cross-selling-wrapper__show"
              )
            ) {
              setTimeout(() => {
                cartDrawerInner.classList.add("has-cross-selling");
              }, 500); // Delay addition by 0.5 seconds
            } else {
              cartDrawerInner.classList.remove("has-cross-selling");
            }
          }
        }
      });

      observer.observe(this.wrapperElement, { attributes: true });
    }
  }

  getPromoCode() {
    return knocking.getCookie("promo_code");
  }

  showWrapper() {
    // console.log("showing wrapper");
    if (!this.wrapperElement) {
      // console.log("Cannot hide wrapper element...");
      return;
    }
    this.wrapperElement.classList.remove("cross-selling-wrapper__hide");
    this.wrapperElement.classList.add("cross-selling-wrapper__show");
    this.wrapperElement.inert = false;
    this.wrapperElement.removeAttribute("aria-hidden");

    const cartDrawerInner = document.querySelector(
      ".cart-drawer__inner-content"
    );
    if (cartDrawerInner) {
      cartDrawerInner.classList.add("has-cross-selling");
    }
  }

  hideWrapper() {
    // console.log("hiding wrapper");
    if (!this.wrapperElement) {
      // console.log("Cannot hide wrapper element...");
      return;
    }
    this.wrapperElement.classList.remove("cross-selling-wrapper__show");
    this.wrapperElement.classList.add("cross-selling-wrapper__hide");
    this.wrapperElement.inert = true;
    this.wrapperElement.setAttribute("aria-hidden", "true");

    const cartDrawerInner = document.querySelector(
      ".cart-drawer__inner-content"
    );
    if (cartDrawerInner) {
      cartDrawerInner.classList.remove("has-cross-selling");
    }
  }
  /**
   * @returns {Promise<void>}
   * */
  async renderVariant() {
    this.fullProduct = await knocking.getProductByHandle(
      this.firstValidCrossSellingVariant.product
    );

    const tags = this.fullProduct.tags;

    this.fullVariant = this.fullProduct.variants.find(
      (variantItem) =>
        variantItem.id === this.firstValidCrossSellingVariant.variant
    );

    if (!this.fullVariant) {
      console.error("Cannot get variant");
      return;
    }
    this.shipsBy = knocking.getTagShippingDate(tags);

    if (this.imageElement) {
      this.imageElement.src = this.fullVariant.featured_image
        ? this.fullVariant.featured_image.src
        : this.fullProduct.featured_image;
    }
    if (this.productTitleElement) {
      this.productTitleElement.textContent =
        this.fullVariant.title === "Default Title"
          ? this.fullProduct.title
          : this.fullVariant.title;
    }
    if (this.productPriceElement) {
      this.productPriceElement.textContent =
        knocking.formatToDollarCurrencyString(this.fullVariant.price);
    }
    if (this.compareAtPriceElement && this.fullVariant.compare_at_price) {
      const compareAtPrice = parseInt(this.fullVariant.compare_at_price) / 100;
      this.compareAtPriceElement.textContent = compareAtPrice
        .toFixed(2)
        .toString();
      this.compareAtPriceElement.removeAttribute("aria-hidden");
    } else if (this.compareAtPriceElement) {
      this.compareAtPriceElement.setAttribute("aria-hidden", "true");
    }

    if (!this.wrapperElement) {
      // console.log("Cannot find cross-selling wrapper element...");
      return;
    }
    this.showWrapper();
  }

  async getValidCrossSellingVariant() {
    this.currentCart = await store.getCart();

    if (!this.currentCart) {
      console.error("Cannot get current cart");
      return;
    }

    this.productsInCart =
      this.currentCart.items.map(
        /** @param {Item} item */ (item) => item.handle
      ) || [];

      // console.log("cross selling variant list: ", this.crossSellingVariantList);

    let validVariant = null;

    for (let i = 0; i < this.crossSellingVariantList.length; i++) {
      // console.log("checking cross selling variant: ", this.crossSellingVariantList[i]);
      let crossSellingVariant = this.crossSellingVariantList[i];
      let variantFound = null;



      if (this.productsInCart && this.productsInCart.length > 0) {
        if (!this.productsInCart.includes(crossSellingVariant.product) && !store.checkDeletedVariantsListForVariant(crossSellingVariant.variant)) {
          variantFound = crossSellingVariant;
        } else {
          continue;
        }
      } else {
          variantFound = crossSellingVariant;
      }

      if (!variantFound) {
        continue;
      }

      const fullProduct = await knocking.getProductByHandle(variantFound.product);
      const fullVariant = fullProduct.variants.find(
        /** @param {Variant} variantItem */ (variantItem) => variantItem.id === variantFound.variant
      );

      if (fullVariant && fullVariant.available) {
      validVariant = variantFound;
      return validVariant;
      } else {
        continue;
      }
    }

    return null;
  }
}

customElements.define(
  "cross-selling-cart-component",
  CrossSellingCartComponent
);
